"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Section } from "@/components/ui/Section";
import { Eyebrow } from "@/components/ui/Eyebrow";
import { SectionTitle } from "@/components/ui/SectionTitle";
import { ease } from "@/lib/motion/easings";

const STAGES = [
  {
    n: "01",
    kicker: "Enter",
    title: "Open Ladder",
    body: "Start at Rookie. Every ranked 1v1 on the live chart moves your rating. No buy-in, just climb.",
  },
  {
    n: "02",
    kicker: "Prove it",
    title: "Qualifiers",
    body: "The top of the ladder earns a seed into the season's tournaments. Form is everything.",
  },
  {
    n: "03",
    kicker: "Compete",
    title: "The Tournaments",
    body: "1v1 brackets, 2v2 Trade Squad, and 80-player Battle Royale, one market, last trader standing.",
  },
  {
    n: "04",
    kicker: "Reign",
    title: "The Championship",
    body: "Win the bracket, take the crown, and put your name on the board for good.",
  },
];

export function RoadToChampion() {
  const reduced = useReducedMotion();
  return (
    <Section tone="base" divided id="road" aria-labelledby="road-title">
      <div className="max-w-3xl">
        <Eyebrow>The road to champion</Eyebrow>
        <SectionTitle id="road-title" accent="to the crown." className="mt-4">
          Four stages
        </SectionTitle>
        <p className="mt-6 font-body text-lg leading-relaxed text-fg-muted">
          No pay-to-win, no shortcuts. The same path for everyone, from your
          first ranked duel to the championship stage.
        </p>
      </div>

      <div className="relative mt-16">
        {/* connecting lane (desktop) */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-0 right-0 top-[52px] hidden h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent lg:block"
        />
        <ol className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {STAGES.map((s, i) => (
            <motion.li
              key={s.n}
              initial={reduced ? false : { opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "0px 0px -12% 0px" }}
              transition={{ duration: 0.6, ease: ease.cinematic, delay: i * 0.12 }}
              className="group relative flex flex-col rounded-lg border border-line bg-elevated/50 p-6 transition-colors duration-300 hover:border-gold/40"
            >
              <div className="relative z-10 flex h-[40px] w-[40px] items-center justify-center rounded-full border border-gold/40 bg-base font-numeric text-sm text-gold tabular-nums shadow-[0_0_24px_rgba(207,174,96,0.18)]">
                {s.n}
              </div>
              <p className="mt-5 font-display text-[10px] uppercase tracking-[0.3em] text-gold-dark">
                {s.kicker}
              </p>
              <h3 className="mt-2 font-display text-2xl uppercase tracking-tight text-fg">
                {s.title}
              </h3>
              <p className="mt-3 font-body text-sm leading-relaxed text-fg-muted">
                {s.body}
              </p>
            </motion.li>
          ))}
        </ol>
      </div>
    </Section>
  );
}
